import React, { useRef, useState, useEffect } from "react";
import { Palette, Move, Layout, Sparkles, RotateCcw, Square, Circle as CircleIcon, Box, X as XIcon } from "lucide-react";

// Hard defaults — must match DEFAULT_LAYOUT in clientCarouselRender.js or the
// preview canvas and the on-screen drag handle will diverge.
// Defaults tuned for "subject-first" caroselli: small card on the LEFT,
// centered vertically, frosted-glass look at 50% opacity so the photo
// behind it stays visible.
const HARD_DEFAULTS = {
  text_color: "#ffffff",
  card_bg_color: "#000000",
  card_bg_alpha: 0.50,
  card_shape: "frosted",
  text_x: 0.20,   // card center at 20% → card spans roughly 2%-37% (with width 0.35)
  text_y: 0.50,   // centered vertically
  card_width: 0.35,
  card_height: 0.22,  // matches DEFAULT_LAYOUT.card_height in clientCarouselRender.js
};

export function resolveLayoutPreview(slide, carousel) {
  const out = { ...HARD_DEFAULTS };
  const cl = carousel?.layout || {};
  const sl = slide?.layout || {};
  for (const k of Object.keys(HARD_DEFAULTS)) {
    if (cl[k] !== undefined && cl[k] !== null) out[k] = cl[k];
    if (sl[k] !== undefined && sl[k] !== null) out[k] = sl[k];
  }
  return out;
}

// =====================================================================
// LAYOUT PANEL — settings UI for the custom-layout mode
// =====================================================================
export function LayoutPanel({ carousel, activeSlide, onToggleMode, onUpdateLayout, onUpdateSlideLayout, savingLayout, hideToggle = false }) {
  const isCustom = carousel.layout_mode === "custom";
  const layout = resolveLayoutPreview(activeSlide, carousel);
  const hasSlideOverride = !!(activeSlide?.layout && Object.keys(activeSlide.layout).length);

  // Local mirrors for the inputs so the color picker doesn't fire a PATCH
  // on every byte the browser yields. We commit on `change` (drag end) and
  // also keep the value flushed on blur.
  const [textColor, setTextColor] = useState(layout.text_color);
  const [cardBg, setCardBg] = useState(layout.card_bg_color);
  const [alpha, setAlpha] = useState(layout.card_bg_alpha);
  const [width, setWidth] = useState(layout.card_width);
  useEffect(() => {
    setTextColor(layout.text_color);
    setCardBg(layout.card_bg_color);
    setAlpha(layout.card_bg_alpha);
    setWidth(layout.card_width);
  }, [carousel?.layout, activeSlide?.layout, activeSlide?.slide_number, layout.text_color, layout.card_bg_color, layout.card_bg_alpha, layout.card_width]);

  // When applying changes, decide whether to write to the slide or to the
  // carousel defaults. Once the slide has any override, NEW edits target
  // the slide (so the user doesn't accidentally globalize a per-slide
  // tweak). Otherwise edits become the carousel-wide default.
  const writeChange = (partial) => {
    if (hasSlideOverride && activeSlide) {
      onUpdateSlideLayout(activeSlide.slide_number, partial);
    } else {
      onUpdateLayout(partial);
    }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-5 mb-5" data-testid="layout-panel">
      {!hideToggle && (
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] flex items-center gap-2">
            <Layout className="w-3 h-3" />
            · Layout personalizzato
          </div>
          <div className="flex items-center gap-2">
            {isCustom && (
              <button
                onClick={() => onUpdateLayout({ ...HARD_DEFAULTS })}
                data-testid="layout-reset-defaults"
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm border border-zinc-700 hover:border-zinc-500 transition-colors"
                title="Ripristina i default di Reelio (vetro · 50% · sinistra)"
              >
                <RotateCcw className="w-3 h-3" />
                Default
              </button>
            )}
            <button
              onClick={onToggleMode}
              disabled={savingLayout}
              data-testid="layout-mode-toggle"
              className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                isCustom
                  ? "bg-[#FF4400] border-[#FF4400] text-white"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
              }`}
            >
              {isCustom ? "ON · Custom" : "OFF · Auto"}
            </button>
          </div>
        </div>
      )}

      {hideToggle && isCustom && (
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] flex items-center gap-2">
            <Layout className="w-3 h-3" />
            · Personalizza il riquadro testo
          </div>
          <button
            onClick={() => onUpdateLayout({ ...HARD_DEFAULTS })}
            data-testid="layout-reset-defaults"
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm border border-zinc-700 hover:border-zinc-500 transition-colors"
            title="Ripristina i default di Reelio (vetro · 50% · sinistra)"
          >
            <RotateCcw className="w-3 h-3" />
            Default
          </button>
        </div>
      )}

      {!isCustom && !hideToggle ? (
        <p className="text-xs text-zinc-500 font-body leading-relaxed">
          Attiva il layout personalizzato per scegliere colori, forma e posizione del testo su tutte le slide.
          La foto di sfondo rimane al naturale, le scritte vengono messe su un riquadro di contrasto trascinabile.
        </p>
      ) : isCustom ? (
        <div className="space-y-4">
          {/* Per-slide override flag */}
          {activeSlide && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-zinc-900 rounded-sm border border-zinc-800">
              <div className="text-xs text-zinc-300 font-body">
                {hasSlideOverride ? (
                  <>Modifiche applicate <strong className="text-[#FF4400]">solo a questa slide</strong></>
                ) : (
                  <>Modifiche applicate <strong>a tutte le slide</strong></>
                )}
              </div>
              {hasSlideOverride && (
                <button
                  onClick={() => onUpdateSlideLayout(activeSlide.slide_number, null)}
                  data-testid="layout-reset-slide"
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm"
                  title="Rimuovi override e usa il default del carosello"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </button>
              )}
            </div>
          )}

          {/* Card shape */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Forma del riquadro</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "rect", label: "Rettangolo", icon: Square },
                { id: "rounded", label: "Arrotondato", icon: Box },
                { id: "frosted", label: "Vetro", icon: CircleIcon },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = layout.card_shape === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => writeChange({ card_shape: opt.id })}
                    data-testid={`layout-shape-${opt.id}`}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-sm border transition-colors ${
                      active
                        ? "border-[#FF4400] bg-[#FF4400]/10 text-[#FF4400]"
                        : "border-zinc-800 hover:border-zinc-600 text-zinc-400"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-mono text-[9px] uppercase tracking-widest">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-3">
            <ColorField
              label="Colore testo"
              value={textColor}
              onChange={setTextColor}
              onCommit={(v) => writeChange({ text_color: v })}
              testId="layout-text-color"
            />
            <ColorField
              label="Colore riquadro"
              value={cardBg}
              onChange={setCardBg}
              onCommit={(v) => writeChange({ card_bg_color: v })}
              testId="layout-bg-color"
            />
          </div>

          {/* Alpha */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Trasparenza riquadro</span>
              <span className="font-mono text-[10px] text-zinc-400">{Math.round(alpha * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(alpha * 100)}
              onChange={(e) => setAlpha(Number(e.target.value) / 100)}
              onMouseUp={() => writeChange({ card_bg_alpha: alpha })}
              onTouchEnd={() => writeChange({ card_bg_alpha: alpha })}
              data-testid="layout-alpha-slider"
              className="w-full accent-[#FF4400]"
            />
          </div>

          {/* Width */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Larghezza riquadro</span>
              <span className="font-mono text-[10px] text-zinc-400">{Math.round(width * 100)}%</span>
            </div>
            <input
              type="range"
              min="20"
              max="100"
              value={Math.round(width * 100)}
              onChange={(e) => setWidth(Number(e.target.value) / 100)}
              onMouseUp={() => writeChange({ card_width: width })}
              onTouchEnd={() => writeChange({ card_width: width })}
              data-testid="layout-width-slider"
              className="w-full accent-[#FF4400]"
            />
          </div>

          <div className="flex items-start gap-2 px-3 py-2 bg-violet-500/10 border border-violet-500/30 rounded-sm">
            <Move className="w-3.5 h-3.5 text-violet-300 mt-0.5 shrink-0" />
            <span className="text-[11px] text-violet-200 font-body leading-relaxed">
              Trascina il riquadro per spostarlo, gli angoli arancioni per ridimensionarlo, oppure la maniglia centrale orizzontale per cambiare la proporzione tra titolo e testo.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ColorField({ label, value, onChange, onCommit, testId }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</div>
      <div className="flex items-center gap-2 p-2 bg-zinc-900 border border-zinc-800 rounded-sm">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit(e.target.value)}
          data-testid={testId}
          className="w-9 h-9 bg-transparent rounded-sm cursor-pointer border-0 p-0"
        />
        <span className="font-mono text-xs text-zinc-300 select-all">{value}</span>
      </div>
    </label>
  );
}

// =====================================================================
// DRAG HANDLE — overlays the slide preview, draggable to move the card,
// resizable from any of the 4 corners.
// =====================================================================
//
// Coords stored:
//   text_x / text_y      → card CENTER as fraction of slide (0..1)
//   card_width           → card width as fraction of slide width
//   card_height          → card height as fraction of slide height
//
// HARD_DEFAULTS provides a card_height (0.22) so the outline and the
// canvas-rendered card always share the same geometry — no drifting.
const MIN_W = 0.15;
const MAX_W = 0.98;
const MIN_H = 0.08;
const MAX_H = 0.92;
// 20px margin matches the clamp inside drawCustomSlide() in
// clientCarouselRender.js (`Math.max(20, Math.min(SLIDE_W - cardW - 20, ...))`).
const EDGE_MARGIN_X = 20 / 1080;   // ≈ 0.0185
const EDGE_MARGIN_Y = 20 / 1350;   // ≈ 0.0148

export function LayoutDragHandle({ slide, carousel, onChange, hasBody = true }) {
  const layout = resolveLayoutPreview(slide, carousel);
  // text_split is optional — pull it directly (resolveLayoutPreview only
  // copies keys in HARD_DEFAULTS). When unset, the renderer applies its
  // length-based heuristic and we render the divider at 0.5 as a hint.
  const persistedSplit =
    typeof (slide?.layout?.text_split) === "number"
      ? slide.layout.text_split
      : typeof (carousel?.layout?.text_split) === "number"
        ? carousel.layout.text_split
        : null;
  const containerRef = useRef(null);
  const startRef = useRef(null);
  const [mode, setMode] = useState(null); // 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br' | 'divider'

  // Box state (uncommitted during drag). When idle, mirrors the persisted
  // layout values so the outline matches the rendered card.
  const [box, setBox] = useState({
    x: layout.text_x,
    y: layout.text_y,
    w: layout.card_width,
    h: layout.card_height,
    s: persistedSplit ?? 0.5,  // fraction of height for headline
  });
  useEffect(() => {
    setBox({
      x: layout.text_x,
      y: layout.text_y,
      w: layout.card_width,
      h: layout.card_height,
      s: persistedSplit ?? 0.5,
    });
  }, [layout.text_x, layout.text_y, layout.card_width, layout.card_height, persistedSplit]);

  const onPointerDown = (which) => (e) => {
    if (!containerRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    const rect = containerRef.current.parentElement.getBoundingClientRect();
    startRef.current = {
      which,
      rect,
      startX: e.clientX,
      startY: e.clientY,
      start: { ...box },
    };
    setMode(which);
  };

  const onPointerMove = (e) => {
    if (!startRef.current) return;
    const { which, rect, startX, startY, start } = startRef.current;
    const dx = (e.clientX - startX) / rect.width;
    const dy = (e.clientY - startY) / rect.height;

    if (which === "move") {
      // Clamp center so the box stays inside the slide AND respects the
      // 20px renderer margin — guarantees the outline never appears to
      // overflow the rendered card after commit.
      const minX = start.w / 2 + EDGE_MARGIN_X;
      const maxX = 1 - start.w / 2 - EDGE_MARGIN_X;
      const minY = start.h / 2 + EDGE_MARGIN_Y;
      const maxY = 1 - start.h / 2 - EDGE_MARGIN_Y;
      const nx = Math.max(minX, Math.min(maxX, start.x + dx));
      const ny = Math.max(minY, Math.min(maxY, start.y + dy));
      setBox({ ...start, x: nx, y: ny });
      return;
    }

    if (which === "divider") {
      // dy is in slide-fraction; convert to card-fraction (divider is
      // expressed as a fraction of the card height, not the slide).
      const cardDY = dy / start.h;
      const ns = Math.max(0.15, Math.min(0.85, start.s + cardDY));
      setBox({ ...start, s: ns });
      return;
    }

    // Resize from a corner — the OPPOSITE corner stays anchored.
    // Compute the start edges, then move the dragged corner; recompute
    // width/height and the new center.
    const left = start.x - start.w / 2;
    const right = start.x + start.w / 2;
    const top = start.y - start.h / 2;
    const bottom = start.y + start.h / 2;

    let newLeft = left, newRight = right, newTop = top, newBottom = bottom;
    if (which === "resize-tl") {
      newLeft = left + dx;
      newTop = top + dy;
    } else if (which === "resize-tr") {
      newRight = right + dx;
      newTop = top + dy;
    } else if (which === "resize-bl") {
      newLeft = left + dx;
      newBottom = bottom + dy;
    } else if (which === "resize-br") {
      newRight = right + dx;
      newBottom = bottom + dy;
    }

    // Clamp edges to the renderer's 20px margins. This ensures the corner
    // handles can't be dragged outside the area where the canvas would
    // actually paint the card.
    newLeft = Math.max(EDGE_MARGIN_X, Math.min(1 - MIN_W - EDGE_MARGIN_X, newLeft));
    newRight = Math.max(MIN_W + EDGE_MARGIN_X, Math.min(1 - EDGE_MARGIN_X, newRight));
    newTop = Math.max(EDGE_MARGIN_Y, Math.min(1 - MIN_H - EDGE_MARGIN_Y, newTop));
    newBottom = Math.max(MIN_H + EDGE_MARGIN_Y, Math.min(1 - EDGE_MARGIN_Y, newBottom));

    let nw = Math.max(MIN_W, Math.min(MAX_W, newRight - newLeft));
    let nh = Math.max(MIN_H, Math.min(MAX_H, newBottom - newTop));
    let nx, ny;
    if (which.endsWith("tl")) { nx = newRight - nw / 2; ny = newBottom - nh / 2; }
    else if (which.endsWith("tr")) { nx = newLeft + nw / 2; ny = newBottom - nh / 2; }
    else if (which.endsWith("bl")) { nx = newRight - nw / 2; ny = newTop + nh / 2; }
    else { nx = newLeft + nw / 2; ny = newTop + nh / 2; }

    setBox({ x: nx, y: ny, w: nw, h: nh });
  };

  const onPointerUp = (e) => {
    if (!startRef.current) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    const { which } = startRef.current;
    startRef.current = null;
    setMode(null);
    const snap = (v) => Math.round(v * 1000) / 1000;
    const final = {};
    if (which === "divider") {
      // Only the split changes when dragging the divider — no positional
      // commit, no width/height commit.
      if (snap(box.s) !== snap(persistedSplit ?? 0.5)) {
        final.text_split = snap(box.s);
      }
    } else {
      if (snap(box.x) !== snap(layout.text_x)) final.text_x = snap(box.x);
      if (snap(box.y) !== snap(layout.text_y)) final.text_y = snap(box.y);
      if (which !== "move") {
        if (snap(box.w) !== snap(layout.card_width)) final.card_width = snap(box.w);
        if (snap(box.h) !== snap(layout.card_height)) final.card_height = snap(box.h);
      }
    }
    if (Object.keys(final).length > 0) onChange(final);
  };

  const isActive = !!mode;
  const cardLeftPct = (box.x - box.w / 2) * 100;
  const cardTopPct = (box.y - box.h / 2) * 100;
  const cardWPct = box.w * 100;
  const cardHPct = box.h * 100;
  // Match the renderer's card radius. The canvas uses 32px for
  // rounded/frosted shapes on a 1080-wide slide; we approximate at the
  // preview scale so the outline curves match what's painted.
  const radius = layout.card_shape === "rect" ? 0 : 18;

  // Corner handles — larger on touch so they're easy to grab
  const HANDLE = 22; // px
  const cornerStyle = (corner) => {
    const base = {
      position: "absolute",
      width: HANDLE,
      height: HANDLE,
      borderRadius: 999,
      background: "#FF4400",
      border: "2px solid #fff",
      boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
      touchAction: "none",
      cursor:
        corner === "tl" || corner === "br" ? "nwse-resize" : "nesw-resize",
    };
    const off = HANDLE / 2;
    if (corner === "tl") return { ...base, left: -off, top: -off };
    if (corner === "tr") return { ...base, right: -off, top: -off };
    if (corner === "bl") return { ...base, left: -off, bottom: -off };
    return { ...base, right: -off, bottom: -off };
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none"
      data-testid="layout-drag-overlay"
    >
      <div
        data-testid="layout-drag-handle"
        className={`absolute pointer-events-auto select-none transition-shadow ${
          isActive
            ? "shadow-2xl shadow-[#FF4400]/40 ring-2 ring-[#FF4400]"
            : "ring-2 ring-white/40 hover:ring-white/70"
        }`}
        style={{
          left: `${cardLeftPct}%`,
          top: `${cardTopPct}%`,
          width: `${cardWPct}%`,
          height: `${cardHPct}%`,
          borderRadius: radius,
          background: "transparent",
          touchAction: "none",
        }}
      >
        {/* Center grab area (move) */}
        <div
          onPointerDown={onPointerDown("move")}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="absolute inset-0 cursor-grab active:cursor-grabbing flex items-center justify-center"
          style={{ touchAction: "none" }}
        >
          <div className="flex flex-col items-center gap-1 px-2 py-1 rounded-full bg-black/55 backdrop-blur-sm pointer-events-none">
            <Move className="w-3 h-3 text-white/90" />
            <span className="font-mono text-[8px] uppercase tracking-widest leading-none text-white/90">
              {mode === "move" ? "Sposta" : "Trascina"}
            </span>
          </div>
        </div>

        {/* 4 corner resize handles */}
        {["tl", "tr", "bl", "br"].map((c) => (
          <div
            key={c}
            data-testid={`layout-resize-${c}`}
            onPointerDown={onPointerDown(`resize-${c}`)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={cornerStyle(c)}
          />
        ))}

        {/* Headline/body divider — a thin horizontal line with a center
            grab pill. Lets the user redistribute vertical space between
            the title and the paragraph. Only shown when the slide has
            body text (otherwise there's nothing to split). */}
        {hasBody && (
          <div
            className="absolute left-0 right-0 pointer-events-none"
            style={{
              top: `${box.s * 100}%`,
              transform: "translateY(-50%)",
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Faint line spanning the full card width */}
            <div
              className="absolute left-2 right-2"
              style={{
                height: 1,
                background: mode === "divider" ? "rgba(255,68,0,0.95)" : "rgba(255,255,255,0.55)",
                boxShadow: mode === "divider" ? "0 0 10px rgba(255,68,0,0.5)" : "none",
              }}
            />
            {/* Center grab pill */}
            <div
              data-testid="layout-divider-handle"
              onPointerDown={onPointerDown("divider")}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              className="pointer-events-auto"
              style={{
                width: 56,
                height: 22,
                borderRadius: 999,
                background: "#FF4400",
                border: "2px solid #fff",
                boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
                cursor: "ns-resize",
                touchAction: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
              }}
              title="Trascina su/giù per dare più spazio al titolo o al testo"
            >
              {/* Two horizontal bars stacked → "drag vertically" hint */}
              <span style={{ display: "block", width: 18, height: 2, background: "#fff", borderRadius: 2 }} />
              <span style={{ display: "block", width: 18, height: 2, background: "#fff", borderRadius: 2 }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
